import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/logo-cloud.png";

const COLORS = {
  primary: {
    main: '#003366',
    dark: '#002244',
    light: '#004488'
  },
  status: {
    success: '#22C55E',
    error: '#EF4444'
  },
  backgrounds: {
    page: '#F9FAFB',
    card: '#FFFFFF',
    highlight: '#F0F5FF',
    muted: '#F5F5F5'
  },
  text: {
    primary: '#1F2937',
    secondary: '#6B7280',
    muted: '#9CA3AF'
  }
};

export default async function downloadSLACertificate(availabilityPercentage, endpointName, totalDownRequests, chartData, topResults) {
  const pdf = new jsPDF("p", "mm", "a4");
  
  const margin = {
    left: 25,
    right: 25,
    top: 20
  };
  const pageWidth = pdf.internal.pageSize.width;
  const contentWidth = pageWidth - margin.left - margin.right;


  // Header background
  pdf.setFillColor(...hexToRGB(COLORS.primary.main));
  pdf.rect(0, 0, pageWidth, 40, 'F');

  // Add logo
  const img = new Image();
  img.src = logo;
  await new Promise((resolve) => {
    img.onload = resolve;
  });

  const logoWidth = 25;
  const logoHeight = 25;
  const logoX = margin.left;
  pdf.addImage(img, 'PNG', logoX, margin.top / 2, logoWidth, logoHeight);

  // Header title
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  const title = "Endpoint Availability Report";
  pdf.text(title, logoX + logoWidth + 10, margin.top + 7);

  // Description section
  const descStartY = 55;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(...hexToRGB(COLORS.text.primary));
  pdf.text(`This report provides a comprehensive overview of the availability metrics for endpoint:`, margin.left, descStartY);
  pdf.setFont("helvetica", "bold");
  pdf.text(endpointName, margin.left, descStartY + 7);
  
  // Metrics cards
  const metricsY = descStartY + 20;
  const cardWidth = (contentWidth - 10) / 2;
  const cardHeight = 50;
  const cardRadius = 3;

  // Availability Card
  pdf.setFillColor(...hexToRGB(COLORS.backgrounds.card));
  pdf.setDrawColor(...hexToRGB(COLORS.primary.light));
  pdf.roundedRect(margin.left, metricsY, cardWidth, cardHeight, cardRadius, cardRadius, 'FD');
  
  // Availability content
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...hexToRGB(COLORS.primary.main));
  pdf.text("Overall Availability", margin.left + (cardWidth / 2), metricsY + 15, { align: 'center' });
  
  pdf.setFontSize(20);
  pdf.setTextColor(...hexToRGB(COLORS.status.success));
  const availText = `${availabilityPercentage.toFixed(2)}%`;
  pdf.text(availText, margin.left + (cardWidth / 2), metricsY + 35, { align: 'center' });

  // Failures Card
  const rightCardX = margin.left + cardWidth + 10;
  pdf.setFillColor(...hexToRGB(COLORS.backgrounds.card));
  pdf.setDrawColor(...hexToRGB(COLORS.primary.light));
  pdf.roundedRect(rightCardX, metricsY, cardWidth, cardHeight, cardRadius, cardRadius, 'FD');
  
  // Failures content
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...hexToRGB(COLORS.primary.main));
  pdf.text("Total Failures", rightCardX + (cardWidth / 2), metricsY + 15, { align: 'center' });
  
  pdf.setFontSize(20);
  pdf.setTextColor(...hexToRGB(COLORS.status.error));
  pdf.text(totalDownRequests.toString(), rightCardX + (cardWidth / 2), metricsY + 35, { align: 'center' });

  // Chart section
  const chartY = metricsY + cardHeight + 25;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.setTextColor(...hexToRGB(COLORS.primary.main));
  pdf.text("Availability Trend", margin.left, chartY);

  const chartHeight = 80;
  const chartAreaY = chartY + 10;
  
  // Draw chart background with subtle border
  pdf.setFillColor(...hexToRGB(COLORS.backgrounds.card));
  pdf.setDrawColor(...hexToRGB(COLORS.text.muted));
  pdf.setLineWidth(0.1);
  pdf.rect(margin.left, chartAreaY, contentWidth, chartHeight, 'FD');

  // Y-axis labels and gridlines
  pdf.setDrawColor(...hexToRGB(COLORS.text.muted));
  pdf.setLineDashPattern([0.5, 0.5], 0);
  [0, 20, 40, 60, 80, 100].forEach(value => {
    const y = chartAreaY + chartHeight - ((value / 100) * (chartHeight - 15));
    pdf.line(margin.left + 20, y, margin.left + contentWidth - 5, y);
    pdf.setFontSize(8);
    pdf.setTextColor(...hexToRGB(COLORS.text.secondary));
    pdf.text(`${value}%`, margin.left + 5, y + 2);
  });
  pdf.setLineDashPattern([], 0);

  // Plot bars
  const barMargin = 25;
  const plotWidth = contentWidth - (2 * barMargin);
  const barWidth = (plotWidth / (chartData.length - 1)) * 0.8;
  
  chartData.forEach((point, i) => {
    const x = margin.left + barMargin + (i * (plotWidth / (chartData.length - 1)));
    const barHeight = (point.y / 100) * (chartHeight - 20);
    const y = chartAreaY + chartHeight - barHeight - 10;

    pdf.setFillColor(...hexToRGB(COLORS.primary.main));
    pdf.rect(x - (barWidth/2), y, barWidth, barHeight, 'F');

    const date = new Date(point.x).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    pdf.setFontSize(8);
    pdf.setTextColor(...hexToRGB(COLORS.text.secondary));
    pdf.text(date, x, chartAreaY + chartHeight - 2, { align: 'center' });
  });
  
 
  // Status history table
  const tableY = chartAreaY + chartHeight + 30;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.setTextColor(...hexToRGB(COLORS.primary.main));
  pdf.text("Recent Status History", margin.left, tableY);

  pdf.autoTable({
    startY: tableY + 10,
    margin: { left: margin.left, right: margin.right },
    head: [["Timestamp", "Status", "Response Time"]],
    body: topResults.slice(0, 10).map(row => [
      new Date(row.timestamp).toLocaleString(),
      row.status,
      `${parseFloat(row.responsetime).toFixed(2)}ms`
    ]),
    styles: {
      fontSize: 9,
      cellPadding: 5,
      lineColor: [...hexToRGB(COLORS.text.muted)],
      lineWidth: 0.1,
      valign: 'middle'
    },
    headStyles: {
      fillColor: [...hexToRGB(COLORS.primary.main)],
      textColor: [...hexToRGB(COLORS.backgrounds.card)],
      fontStyle: 'bold'
    },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 30, halign: 'center' },
      2: { cellWidth: 40, halign: 'right' }
    },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 1) {
        const status = data.cell.raw;
        pdf.setTextColor(
          ...hexToRGB(status === 'Running' ? COLORS.status.success : COLORS.status.error)
        );
      }
    }
  });

  // Footer
  const pageCount = pdf.internal.getNumberOfPages();
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(...hexToRGB(COLORS.text.muted));
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    const timestamp = new Date().toLocaleString();
    pdf.text(
      `Generated by CloudLens on ${timestamp}`,
      margin.left,
      pdf.internal.pageSize.height - 10
    );
    pdf.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - margin.right,
      pdf.internal.pageSize.height - 10,
      { align: 'right' }
    );
  }

  pdf.save(`Endpoint_Report_${new Date().toISOString().split('T')[0]}.pdf`);
}

function hexToRGB(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}